/**
 * Supported UI locales. The `is_rtl` flag is owned by the `locales` DB table; the
 * frontend gets it via `useDirection()` rather than baking it into this enum.
 */
export const LOCALES = ['en', 'ar'] as const;
export type Locale = (typeof LOCALES)[number];

export function isLocale(value: unknown): value is Locale {
  return typeof value === 'string' && (LOCALES as readonly string[]).includes(value);
}

/**
 * Permission codes deliberately do NOT live here.
 *
 * There was a `PERMISSIONS` list in this file, described as "add a
 * permission = add to this list + a migration". The second half kept
 * happening and the first half stopped: it ended up 46 codes against the 141
 * that really exist, and nothing imported it — `RoleCreateSchema` validates
 * `permissionCodes` as plain strings, and `requirePermission` takes a plain
 * string too. A stale list nobody reads is worse than no list, so it is gone.
 *
 * The catalogue is the `permissions` TABLE, created by migrations and copied
 * into each tenant DB by seedCatalogsInto. `infra/db/catalogs.ts` seeds
 * descriptions for most of it but is not authoritative either.
 */

/**
 * ISO 4217 currency codes seeded in Phase 1. The full set lives in the `currencies` table;
 * this enum is for API validation where the codes must be known at type-check time.
 */
export const SEEDED_CURRENCIES = [
  'USD', 'EUR', 'GBP', 'JPY', 'CHF', 'CAD', 'AUD', 'CNY', 'INR',
  'TRY', 'IQD', 'AED', 'SAR', 'JOD', 'KWD', 'BHD', 'QAR', 'OMR', 'EGP', 'LBP',
] as const;

export type SeededCurrency = (typeof SEEDED_CURRENCIES)[number];

/**
 * How many EXTRA scannable codes one item — or one of its packing units — may
 * carry, beyond the primary barcode.
 *
 * A single number, imported by the item form as well as the schemas, because it
 * used to be seven separate `50`s: three in zod and four in ItemFormPage.
 * Raising it meant finding all seven, and missing one gives the worst possible
 * failure — a form that lets you paste a hundred codes and a server that then
 * refuses the save.
 *
 * Lives in this leaf module rather than beside the item schemas because
 * `item.ts` already imports `itemUnit.ts`; putting it in either would make the
 * two import each other, and since these are module-level consts the cycle
 * fails at load with a TDZ error rather than anywhere informative.
 *
 * 120 because a shop re-labelling the same article across print runs and
 * suppliers genuinely accumulates that many. The cap is there to stop a runaway
 * paste, not to ration real codes.
 */
export const MAX_ITEM_BARCODES = 120;
